import { ReactNode, useEffect, useState, createContext, useCallback, useRef } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { auth0Config, logAuthEvent, AUTH0_UI_LOCALE } from '../../../config/auth.config';
import type { AuthContextType, AppUser, User } from '../types/auth.types';
import { authService } from '../services/authService';
import { ForbiddenError } from '../../../lib/apiClient';
import { clearSessionAndRedirect, isAuthenticationError, retryCounter } from '../../../lib/sessionManager';
import { captureException, setSentryUser } from '../../../config/sentry.config';
import { VISIBILITY_RECHECK_COOLDOWN_MS } from '../../../config/appConfig';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: 'openid profile email',
        ui_locales: AUTH0_UI_LOCALE,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AuthContextProvider>{children}</AuthContextProvider>
    </Auth0Provider>
  );
};

const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const auth0 = useAuth0();

  // Refs for stability — prevents re-render loops
  const userRef = useRef(auth0.user);
  const getAccessTokenSilentlyRef = useRef(auth0.getAccessTokenSilently);
  const tokenRequestRef = useRef<Promise<string | undefined> | null>(null);
  const hasLoadedProfileRef = useRef(false);
  const lastProfileCheckRef = useRef<number>(0);

  useEffect(() => { userRef.current = auth0.user; }, [auth0.user]);
  useEffect(() => { getAccessTokenSilentlyRef.current = auth0.getAccessTokenSilently; }, [auth0.getAccessTokenSilently]);

  // App profile — populated by GET /auth/login after successful Auth0 authentication
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    if (tokenRequestRef.current) {
      console.log('[AuthContext] Token request in progress, reusing...');
      return tokenRequestRef.current;
    }

    const retryKey = 'getAccessToken';

    const tokenPromise = (async () => {
      try {
        const retryCount = retryCounter.increment(retryKey);

        if (retryCounter.hasExceededLimit(retryKey)) {
          const errorMessage = `Token retry limit exceeded (${retryCount} attempts)`;
          console.error('[AuthContext]', errorMessage);
          captureException(new Error(errorMessage), { retryCount, userId: userRef.current?.sub });
          clearSessionAndRedirect('Too many failed token attempts');
          return undefined;
        }

        const token = await getAccessTokenSilentlyRef.current();
        retryCounter.reset(retryKey);
        return token;

      } catch (error) {
        logAuthEvent('Error getting access token', error);
        console.error('[AuthContext] Token error:', error);

        if (isAuthenticationError(error)) {
          console.error('[AuthContext] Auth error detected, clearing session');
          captureException(error instanceof Error ? error : new Error('Auth error'), {
            errorType: 'authentication',
            userId: userRef.current?.sub,
          });
          clearSessionAndRedirect('Invalid or expired tokens');
          return undefined;
        }

        captureException(error instanceof Error ? error : new Error('Token error'), {
          errorType: 'token_retrieval',
          userId: userRef.current?.sub,
        });
        return undefined;
      } finally {
        tokenRequestRef.current = null;
      }
    })();

    tokenRequestRef.current = tokenPromise;
    return tokenPromise;
  }, []); // Empty deps — uses refs for stability

  /**
   * Calls GET /auth/login to fetch the app-level user profile.
   * Replaces both the old /auth/is_first check and the admin status detection.
   */
  const loadProfile = useCallback(async (silent = false) => {
    if (!silent) setIsLoadingProfile(true);
    setProfileError(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        // Bailing out silently here would leave both `appUser` and
        // `profileError` empty — a state ProtectedRoute now reads as "the
        // profile is still on its way", i.e. a spinner that never resolves.
        // Only surface it on the initial load; a failed silent re-check should
        // not throw an error screen at someone who is working.
        if (!silent) {
          setProfileError('Could not verify your session. Please sign in again.');
        }
        return;
      }

      const profile = await authService.login(token);
      setAppUser(profile);
      hasLoadedProfileRef.current = true;
      lastProfileCheckRef.current = Date.now();
      logAuthEvent('User profile loaded', {
        rol: profile.rol,
        isFirst: profile.is_first,
      });
    } catch (err) {
      hasLoadedProfileRef.current = true; // Prevent infinite retry on persistent error

      if (err instanceof ForbiddenError) {
        const msg = err.message;
        if (msg.includes('suspended') || msg.includes('Account suspended')) {
          setProfileError(
            'Your account has been suspended. Contact your administrator.'
          );
        } else {
          // "Acceso no autorizado. Contactá al administrador." or similar
          setProfileError(
            'Your account is not authorized to access this application. Contact your administrator.'
          );
        }
      } else {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load user profile';
        setProfileError(errorMessage);
        captureException(err instanceof Error ? err : new Error('Profile load failed'), {
          userId: userRef.current?.sub,
        });
      }
    } finally {
      if (!silent) setIsLoadingProfile(false);
    }
  }, [getAccessToken]);

  // Load profile once after successful Auth0 authentication
  useEffect(() => {
    if (auth0.isAuthenticated && auth0.user && !hasLoadedProfileRef.current) {
      logAuthEvent('User authenticated', { email: auth0.user.email, name: auth0.user.name });
      setSentryUser({
        id: auth0.user.sub || 'unknown',
        email: auth0.user.email,
        name: auth0.user.name,
      });
      loadProfile();
    }

    if (auth0.error) {
      logAuthEvent('Auth error', auth0.error);
      console.error('[Auth0] Authentication error:', auth0.error);
    }
  }, [auth0.isAuthenticated, auth0.user, auth0.error, loadProfile]);

  // Reset profile state on logout
  useEffect(() => {
    if (!auth0.isAuthenticated) {
      setAppUser(null);
      setProfileError(null);
      hasLoadedProfileRef.current = false;
      lastProfileCheckRef.current = 0;
    }
  }, [auth0.isAuthenticated]);

  // Re-check profile when the user returns to the tab — catches suspension done while away.
  // Cooldown of 2 minutes to avoid hammering the backend on every tab switch.
  useEffect(() => {
    if (!auth0.isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastProfileCheckRef.current;
      if (elapsed < VISIBILITY_RECHECK_COOLDOWN_MS) return;
      logAuthEvent('Tab refocused — re-checking profile for suspension');
      loadProfile(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [auth0.isAuthenticated, loadProfile]);

  const contextValue: AuthContextType = {
    user: auth0.user as User | undefined,
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    error: auth0.error,

    appUser,
    isAdmin: appUser?.rol === 'admin' || appUser?.rol === 'super_admin',
    isSuperAdmin: appUser?.rol === 'super_admin',
    isLoadingProfile,
    profileError,

    // null while loading, boolean once profile is fetched
    isFirstLogin: hasLoadedProfileRef.current ? (appUser?.is_first ?? false) : null,

    getAccessToken,
    checkFirstLogin: loadProfile,
    refreshProfile: loadProfile,
    retryFirstLoginCheck: async () => {
      hasLoadedProfileRef.current = false;
      await loadProfile();
    },

    loginWithRedirect: async (options: { organization?: string | null; prompt?: string } = {}) => {
      logAuthEvent('Login initiated', options);
      const authorizationParams: Record<string, string> = {};
      if (options.organization !== null) {
        const org = options.organization || auth0Config.organizationId;
        if (org) authorizationParams.organization = org;
      }
      if (options.prompt) {
        authorizationParams.prompt = options.prompt;
      }
      return auth0.loginWithRedirect({
        authorizationParams:
          Object.keys(authorizationParams).length > 0 ? authorizationParams : undefined,
      });
    },

    logout: async (options: { returnTo?: string } = {}) => {
      logAuthEvent('Logout initiated');
      return auth0.logout({
        logoutParams: { returnTo: options.returnTo || auth0Config.redirectUri },
      });
    },

    logoutAndRedirectToLogin: async () => {
      logAuthEvent('Forced logout after password change');
      try {
        return await auth0.logout({
          logoutParams: { returnTo: window.location.origin },
        });
      } catch (error) {
        console.error('[AuthContext] auth0.logout failed:', error);
        throw error;
      }
    },
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
